<template>
  <dialog-quickshop>
    <template #express-payments="{ selectedVariant }" v-if="$feature.tempShowExpressPaymentsInQuickshop">
      <div
        class="grid space-x-2"
        :class="{ 'cols-2': isApplePayEnabled && isPaypalButtonEnabled, 'mt-2': isApplePayEnabled || isPaypalButtonEnabled }"
      >
        <checkout-apple-pay-button
          v-if="isApplePayEnabled"
          class="w-full"
          :class="{ 'h-full': isApplePayEnabled && isPaypalButtonEnabled }"
          :style="isPaypalButtonEnabled && paypalStyle?.height && `max-height: ${pxToRem(paypalStyle?.height)}`"
          @click="triggerApplePaySession(selectedVariant)"
        />
        <form-paypal-adyen
          v-if="isPaypalButtonEnabled"
          class="w-full"
          context="paypalPdp"
          is-express-flow
          is-same-as-shipping
          :locale
          :meta
          :selected-variant
        />
      </div>
    </template>
  </dialog-quickshop>
</template>

<script lang="ts" setup>
import type { Emits as DialogQuickshopEmits, Props as DialogQuickshopProps } from '#brand/components/dialog/quickshop/Quickshop.vue'

import type { GetPaymentSessionData } from '#root/api/clients/cart/data-contracts'
import DialogQuickshop from '#brand/components/dialog/quickshop/Quickshop.vue'

export type Props = DialogQuickshopProps
export type Emits = DialogQuickshopEmits

const { $feature, $gtm } = useNuxtApp()
const paypalStyle = useAppConfig().components.paypal.button.style
const { $getPaymentSession } = useApi().cart
const auth = useAuthStore()

const locale = ref<string>()
const meta = ref<GetPaymentSessionData['meta']>()
const isAdyenEnabled = ref(false)
const paymentProvider = ref<GetPaymentSessionData['provider']>()

const isPaypalButtonEnabled = computed(() =>
  $feature.tempShowExpressPaymentsInQuickshop && isAdyenEnabled.value
  && !auth.inStoreEmployeeLoggedIn
)

const isApplePayEnabled = computed(() =>
  $feature.tempShowExpressPaymentsInQuickshop
  && window.ApplePaySession
)

const triggerApplePaySession = (selectedVariant) => {
  try {
    const payload = { productId: selectedVariant.id, upc: selectedVariant.upc }

    $gtm.push('checkout.onLoadEventData', 'APPLE_PAY')

    // initiate the temporary applePay only after clicking the button
    const { initApplePaySession } = useApplePay('applePayPdp')
    initApplePaySession(payload)
  }
  catch (e) {
    log.info(e)
  }
}

const getPaymentSession = async () => {
  try {
    const session = await $getPaymentSession({
      referer: window.location.origin,
      paymentMethodId: 'CREDIT_CARD',
      paymentMethod: 'paypal'
    }, {
      retryDelay: 3000,
      headers: {
        'content-type': 'application/json'
      }
    })

    if (!session.success)
      throw session

    paymentProvider.value = session.provider
    meta.value = session.meta
    locale.value = session.locale
  }
  catch (e) {
    log.error(e)
  }
}

// Once ADYEN is the ONLY provider this condition could be removed
watch(() => paymentProvider.value, () => {
  if (paymentProvider.value === 'ADYEN')
    isAdyenEnabled.value = true
})

onMounted(() => {
  // get payment provider
  getPaymentSession()
})
</script>
